/**
 * F-505-FE-07: SessionContext
 *
 * Active session state for the duration of a clinical consultation:
 * - sessionId, status, doctor/patient language codes
 * - templateVersionId
 * - VAD state (idle / doctor_speaking / patient_speaking / tts_playing)
 * - Transcript segments array
 * - Safety flags (held TTS, pending confirmations)
 * - Headset status for both doctor and patient devices
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SessionStatus =
  | 'IDLE'
  | 'PAIRING'
  | 'ACTIVE'
  | 'FLAGGED'
  | 'ESCALATED'
  | 'ENDING'
  | 'CLOSED'
  | 'NOTE_REVIEW'
  | 'DRAFT'
  | 'COMPLETED'
  | 'EXPORTED';

export type VADState =
  | 'idle'
  | 'doctor_speaking'
  | 'patient_speaking'
  | 'tts_playing';

export type TranscriptDirection = 'doctor_to_patient' | 'patient_to_doctor';

export type SafetyLevel = 'green' | 'amber' | 'red';

export type DegradationLevel = 'none' | 'translation' | 'transcript_only';

export interface TranscriptSegment {
  segmentId: string;
  direction: TranscriptDirection;
  originalText: string;
  translatedText: string;
  /** Set when the LLM issues a correction for this segment via transcript:correction WS message. */
  correctedText?: string;
  safetyLevel: SafetyLevel;
  safetyFlags: string[];
  timestamp: string;
  /** Degradation level — indicates if a fallback translation method was used. */
  degradationLevel?: DegradationLevel;
  /** True when the utterance was flagged as incomplete — patient paused mid-thought. */
  incomplete?: boolean;
  /** Pipeline sequence number — used for ordered display when concurrent pipelines complete out of order. */
  seq?: number;
}

export interface HeldSafetyItem {
  segmentId: string;
  originalText: string;
  translatedText: string;
  flags: string[];
  timestamp: string;
}

export interface HeadsetStatus {
  doctor: boolean;
  patient: boolean;
}

export interface SessionState {
  sessionId: string | null;
  status: SessionStatus | null;
  doctorLanguageCode: string | null;
  patientLanguageCode: string | null;
  templateVersionId: string | null;
  vadState: VADState;
  segments: TranscriptSegment[];
  heldSafetyItems: HeldSafetyItem[];
  headsetStatus: HeadsetStatus;
  pipelineMode: 'batch' | 'streaming' | 'full_stream' | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type SessionAction =
  | { type: 'SET_SESSION'; payload: Partial<SessionState> }
  | { type: 'SET_STATUS'; status: SessionStatus }
  | { type: 'SET_VAD_STATE'; vadState: VADState }
  | { type: 'APPEND_SEGMENT'; segment: TranscriptSegment }
  | { type: 'UPDATE_SEGMENT'; segmentId: string; correctedText: string }
  | { type: 'SET_HEADSET_STATUS'; device: 'doctor' | 'patient'; connected: boolean }
  | { type: 'HOLD_SAFETY_ITEM'; item: HeldSafetyItem }
  | { type: 'CLEAR_HELD_SAFETY_ITEM'; segmentId: string }
  | { type: 'CLEAR_SESSION' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState: SessionState = {
  sessionId: null,
  status: null,
  doctorLanguageCode: null,
  patientLanguageCode: null,
  templateVersionId: null,
  vadState: 'idle',
  segments: [],
  heldSafetyItems: [],
  headsetStatus: { doctor: false, patient: false },
  pipelineMode: null,
};

function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'SET_SESSION':
      return { ...state, ...action.payload };

    case 'SET_STATUS':
      return { ...state, status: action.status };

    case 'SET_VAD_STATE':
      return { ...state, vadState: action.vadState };

    case 'APPEND_SEGMENT': {
      const newSeg = action.segment;
      // If the new segment has a seq number, insert in order.
      // Otherwise (legacy/reconnect), append at end.
      if (newSeg.seq !== undefined) {
        const segments = [...state.segments];
        // Find insertion point — first segment with a higher seq, or end of array
        let insertIdx = segments.length;
        for (let i = segments.length - 1; i >= 0; i--) {
          if (segments[i]!.seq !== undefined && segments[i]!.seq! >= newSeg.seq!) {
            insertIdx = i;
          } else {
            break;
          }
        }
        segments.splice(insertIdx, 0, newSeg);
        return { ...state, segments };
      }
      return { ...state, segments: [...state.segments, newSeg] };
    }

    case 'UPDATE_SEGMENT':
      return {
        ...state,
        segments: state.segments.map((s) =>
          s.segmentId === action.segmentId
            ? { ...s, translatedText: action.correctedText, correctedText: action.correctedText }
            : s,
        ),
      };

    case 'SET_HEADSET_STATUS':
      return {
        ...state,
        headsetStatus: {
          ...state.headsetStatus,
          [action.device]: action.connected,
        },
      };

    case 'HOLD_SAFETY_ITEM':
      return {
        ...state,
        heldSafetyItems: [...state.heldSafetyItems, action.item],
      };

    case 'CLEAR_HELD_SAFETY_ITEM':
      return {
        ...state,
        heldSafetyItems: state.heldSafetyItems.filter(
          (i) => i.segmentId !== action.segmentId,
        ),
      };

    case 'CLEAR_SESSION':
      return { ...initialState };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context value interface
// ---------------------------------------------------------------------------

export interface SessionContextValue extends SessionState {
  setSession: (partial: Partial<SessionState>) => void;
  setStatus: (status: SessionStatus) => void;
  setVadState: (vadState: VADState) => void;
  appendSegment: (segment: TranscriptSegment) => void;
  updateSegment: (segmentId: string, correctedText: string) => void;
  setHeadsetStatus: (device: 'doctor' | 'patient', connected: boolean) => void;
  holdSafetyItem: (item: HeldSafetyItem) => void;
  clearHeldSafetyItem: (segmentId: string) => void;
  clearSession: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SessionContext = createContext<SessionContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sessionReducer, initialState);

  const setSession = useCallback((partial: Partial<SessionState>) => {
    dispatch({ type: 'SET_SESSION', payload: partial });
  }, []);

  const setStatus = useCallback((status: SessionStatus) => {
    dispatch({ type: 'SET_STATUS', status });
  }, []);

  const setVadState = useCallback((vadState: VADState) => {
    dispatch({ type: 'SET_VAD_STATE', vadState });
  }, []);

  const appendSegment = useCallback((segment: TranscriptSegment) => {
    dispatch({ type: 'APPEND_SEGMENT', segment });
  }, []);

  const updateSegment = useCallback((segmentId: string, correctedText: string) => {
    dispatch({ type: 'UPDATE_SEGMENT', segmentId, correctedText });
  }, []);

  const setHeadsetStatus = useCallback(
    (device: 'doctor' | 'patient', connected: boolean) => {
      dispatch({ type: 'SET_HEADSET_STATUS', device, connected });
    },
    [],
  );

  const holdSafetyItem = useCallback((item: HeldSafetyItem) => {
    dispatch({ type: 'HOLD_SAFETY_ITEM', item });
  }, []);

  const clearHeldSafetyItem = useCallback((segmentId: string) => {
    dispatch({ type: 'CLEAR_HELD_SAFETY_ITEM', segmentId });
  }, []);

  const clearSession = useCallback(() => {
    dispatch({ type: 'CLEAR_SESSION' });
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      ...state,
      setSession,
      setStatus,
      setVadState,
      appendSegment,
      updateSegment,
      setHeadsetStatus,
      holdSafetyItem,
      clearHeldSafetyItem,
      clearSession,
    }),
    [
      state,
      setSession,
      setStatus,
      setVadState,
      appendSegment,
      updateSegment,
      setHeadsetStatus,
      holdSafetyItem,
      clearHeldSafetyItem,
      clearSession,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return ctx;
}
